export type MessageSubject = {
  projectId: number
  subjectId: number
}

export enum MessageType {
  PatientCommunication = 'PatientCommunication',
  Message = 'Message',
}

export enum MessageReadStatus {
  Read = 'read',
  Unread = 'unread',
}

export type UnreadMessageCountResponse = {
  count: number
}

export type BaseMessage = {
  /**
   * The id of the message
   */
  id: string
  /**
   * The title of the message
   */
  title: string
  /**
   * The subject id of the message
   */
  subjectId: number
  /**
   * The project id of the message
   */
  projectId: number
  /**
   * The read status of the message
   */
  readStatus: MessageReadStatus
  /**
   * Trigger of the message (unique identifier shared by multiple activities or document flows to relate to)
   * @example 'appointment_123'
   * @example 'encounter_456'
   * @example 'careplan_789'
   */
  trigger?: string
  /**
   * The careplan activity id of the message
   */
  careplanActivityId: number
  /**
   * The created at date of the message
   */
  createdAt: Date
  /**
   * The updated at date of the message
   */
  updatedAt: Date
  /**
   * The date of the message
   */
  date: Date
}

export type MessageResponse = {
  data: Message[]
  page: number
  total: number
  limit: number
}

export type InfoMessage = BaseMessage & {
  type: MessageType.Message
  body: string
  readAt: Date | null
  format: 'text' | 'html'
  attachments: MessageAttachment[]
}

// PatientCommunication typing

export type BaseMessageAttachment = {
  name: string
  extension: string
}

export type MessageDocumentAttachment = BaseMessageAttachment & {
  attachmentType: 'Document'
  fileId: number
}

export type MessageBrochureAttachment = BaseMessageAttachment & {
  attachmentType: 'Brochure'
  fileUrl: string
}

export type MessageAttachment = MessageDocumentAttachment | MessageBrochureAttachment

export type PatientTarget = {
  name: string
  type: 'patient'
  actorId: number // HB Actor id
  id?: number // HB Subject id
}

export type PatientCommunicationMessage = {
  id: string
  hidden: boolean
  sender: PatientTarget | CaregiverTarget
  content: string
  receiver: PatientTarget | CaregiverTarget
  readStatus: {
    patient: string | null
    caregiver: string | null
  }
  createdAt: string
  attachments?: MessageAttachment[]
}

export type SpecialisationInfo = {
  id: number
  agendaId: string | null
  title: {
    nl?: string
    en?: string
  }
}

export type CaregiverTarget = {
  name: string
  type: 'doctor' | 'hospital' | 'caregiver'
  userId: number // Synops User id
  specialisationId?: number // Synops Specialisation id
  specialisationInfo?: SpecialisationInfo
  doctorInfo?: DoctorInfo
}

export type DoctorInfo = {
  name: string
  agendaId: string | null
}

export type PatientCommunication = BaseMessage & {
  type: MessageType.PatientCommunication
  category: string
  messages: PatientCommunicationMessage[]
}

// Union Type
export type Message = InfoMessage | PatientCommunication
